import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import RedeemIcon from '@mui/icons-material/Redeem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { userApiClient } from '../../userApiClient';
import { RewardsSummary, RewardTransaction, RewardSource } from '../../models/Rewards';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { format, Locale } from 'date-fns';
import { enGB, pl as plPL } from 'date-fns/locale';

const dateLocales: { [key: string]: Locale } = {
  en: enGB,
  pl: plPL,
  // Fallback to enGB for other languages
};

export default function RewardsPage() {
  const { t, i18n } = useTranslation();
  const [rewards, setRewards] = useState<RewardsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voucherCode, setVoucherCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState(false);

  const fetchRewards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userApiClient.getRewards();
      setRewards(data);
    } catch (err: any) {
      console.error('Error fetching rewards:', err);
      setError(
        err.response?.data?.message ||
        t('rewards.fetchError', 'Failed to load rewards. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  // Remove dashes from gift card code
  const removeDashes = (code: string): string => {
    return code.replace(/-/g, '');
  };

  // Format gift card code as XXXX-XXXX-XXXX
  const formatGiftCardCode = (code: string): string => {
    const cleanCode = removeDashes(code);
    // Only format if we have at least some characters
    if (cleanCode.length === 0) return '';
    
    // Format as XXXX-XXXX-XXXX
    const parts: string[] = [];
    for (let i = 0; i < cleanCode.length; i += 4) {
      parts.push(cleanCode.slice(i, i + 4));
    }
    return parts.join('-');
  };

  const handleRedeemGiftCard = async () => {
    const cleanCode = removeDashes(voucherCode).trim();
    if (!cleanCode) {
      setRedeemError(t('rewards.giftCardCodeRequired', 'Please enter a gift card code'));
      return;
    }

    try {
      setRedeeming(true);
      setRedeemError(null);
      setRedeemSuccess(false);
      
      await userApiClient.redeemVoucher({ voucherCode: cleanCode });
      
      setRedeemSuccess(true);
      setVoucherCode('');
      
      // Refresh rewards data after successful redemption
      await fetchRewards();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setRedeemSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Error redeeming gift card:', err);
      setRedeemError(
        err.response?.data?.message ||
        t('rewards.redeemError', 'Failed to redeem gift card. Please check the code and try again.')
      );
    } finally {
      setRedeeming(false);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const locale = dateLocales[i18n.language] || enGB;
      return format(date, 'PPp', { locale });
    } catch (err) {
      // Fallback to simple date format if formatting fails
      return new Date(dateString).toLocaleString(i18n.language);
    }
  };

  const getSourceLabel = (source: RewardSource) => {
    switch (source) {
      case RewardSource.VOUCHER_REDEMPTION:
        return t('rewards.source.giftCardRedemption', 'Gift Card Redemption');
      case RewardSource.GOAL_ACHIEVEMENT:
        return t('rewards.source.goalAchievement', 'Goal Achievement');
      default:
        return source;
    }
  };

  const getSourceColor = (source: RewardSource): 'success' | 'info' | 'default' => {
    switch (source) {
      case RewardSource.VOUCHER_REDEMPTION:
        return 'success';
      case RewardSource.GOAL_ACHIEVEMENT:
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading && !rewards) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <SEO
        title={t('rewards.title', 'Rewards Center')}
        description={t('rewards.description', 'View your rewards balance and transaction history')}
      />

      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {t('rewards.title', 'Rewards Center')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Balance Card */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, #4E67EB 0%, #465AD9 100%)',
          color: 'white',
          boxShadow: '0px 8px 32px rgba(78, 103, 235, 0.3)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                {t('rewards.currentBalance', 'Current Balance')}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {rewards
                  ? formatCurrency(rewards.balance.amount, rewards.balance.currencyCode)
                  : formatCurrency(0, 'USD')}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Gift Card Redeem Section */}
        <Box sx={{ flex: { xs: '1', md: '0 0 400px' }, display: 'flex' }}>
          <Card sx={{ mb: 3, flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <RedeemIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('rewards.redeemGiftCard', 'Redeem Gift Card')}
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('rewards.redeemDescription', 'Enter your gift card code below to add rewards to your account.')}
              </Typography>

              {redeemSuccess && (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 2 }}
                  onClose={() => setRedeemSuccess(false)}
                >
                  {t('rewards.redeemSuccess', 'Gift card redeemed successfully! Your balance has been updated.')}
                </Alert>
              )}

              {redeemError && (
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{ mb: 2 }}
                  onClose={() => setRedeemError(null)}
                >
                  {redeemError}
                </Alert>
              )}

              <Stack spacing={2} sx={{ mt: 'auto' }}>
                <TextField
                  label={t('rewards.giftCardCode', 'Gift Card Code')}
                  value={formatGiftCardCode(voucherCode)}
                  onChange={(e) => {
                    // Remove dashes from input and update state
                    const cleanValue = removeDashes(e.target.value);
                    setVoucherCode(cleanValue);
                    setRedeemError(null);
                    setRedeemSuccess(false);
                  }}
                  placeholder="XXXX-XXXX-XXXX"
                  fullWidth
                  disabled={redeeming}
                  InputProps={{
                    startAdornment: (
                      <CardGiftcardIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !redeeming) {
                      handleRedeemGiftCard();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleRedeemGiftCard}
                  disabled={redeeming || !removeDashes(voucherCode).trim()}
                  startIcon={redeeming ? <CircularProgress size={20} /> : <RedeemIcon />}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {redeeming
                    ? t('rewards.redeeming', 'Redeeming...')
                    : t('rewards.redeem', 'Redeem Gift Card')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Transactions Section */}
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Card sx={{ mb: 3, flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('rewards.rewardsHistory', 'Rewards History')}
                </Typography>
              </Stack>

              <Box sx={{ flex: 1 }}>
                {loading && rewards ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !rewards || rewards.transactions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" color="text.secondary">
                      {t('rewards.noTransactions', 'No transactions yet')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('rewards.noTransactionsDescription', 'Your reward transactions will appear here')}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {t('rewards.date', 'Date')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {t('rewards.source.label', 'Source')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {t('rewards.description', 'Description')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {t('rewards.amount', 'Amount')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rewards.transactions.map((transaction: RewardTransaction) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(transaction.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getSourceLabel(transaction.source)}
                                color={getSourceColor(transaction.source)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {transaction.description || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'success.main',
                                }}
                              >
                                +{formatCurrency(transaction.amount, transaction.currencyCode)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
}

